import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	Outlet,
	useLocation,
	useNavigate,
	useParams,
	useSearch,
} from "@tanstack/react-router";
import {
	Calendar,
	CalendarCheck01,
	Hash01,
	Maximize02,
	Trash01,
} from "@untitledui/icons";
import { useEffect, useMemo, useState } from "react";
import z from "zod";
import { DeleteJobModal } from "@/components/Organisms/DeleteJobModal/DeleteJobModal";
import { DestinyEntityDrawer } from "@/components/Organisms/DestinyEntityDrawer/DestinyEntityDrawer";
import {
	DetailsState,
	DetailsTab,
} from "@/components/Organisms/Details/Details";
import { DrawerProvider } from "@/contexts/drawer-context";
import {
	AssigneeTypeFilter,
	type AssigneeTypeFilterValue,
	JobEntityDetails,
} from "@/features/jobs/components";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useDestinyDrawerCurrentTab } from "@/lib/use-destiny-drawer-current-tab";
import { authQueryOptions } from "@/services/auth";
import { jobQueryOptions } from "@/services/job";
import {
	createdMetadataLabel,
	updatedMetadataLabel,
} from "@/utils/entity-metadata-date-label";

export const TabSchema = z.enum(["details", "assignee", "activity"]);

export const AssigneeTypeSchema = z.enum(["all", "users", "subcontractors"]);

export const SearchSchema = z.object({
	tab: TabSchema.optional(),
	organizationId: z.string().optional(),
	jobId: z.string().optional(),
	delete: z.boolean().optional(),
	/** Assignees tab type filter (All / Users / Subcontractors). */
	assigneeType: AssigneeTypeSchema.optional(),
});

export type Tab = z.infer<typeof TabSchema>;
export type Search = z.infer<typeof SearchSchema>;

export const Route = createFileRoute(
	"/_authenticated/(web-app)/_web/web/$orgId/jobs/(job)/$jobId/_drawer",
)({
	component: RouteComponent,
	validateSearch: (search) => {
		return SearchSchema.parse(search);
	},
});

function RouteComponent() {
	return (
		<DrawerProvider>
			<JobDrawer />
		</DrawerProvider>
	);
}

/**
 * Job detail drawer — DestinyEntityDrawer (type=job) + JobEntityDetails
 * (Details / Assignees / Activity).
 */
function JobDrawer() {
	const navigate = useNavigate();
	const location = useLocation();
	const { t } = useAppTranslation();
	const params = useParams({
		from: "/_authenticated/(web-app)/_web/web/$orgId/jobs/(job)/$jobId/_drawer",
	});
	const search = useSearch({
		from: "/_authenticated/(web-app)/_web/web/$orgId/jobs/(job)/$jobId/_drawer",
	});
	const [isOpen, setIsOpen] = useState(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

	const currentTab = useDestinyDrawerCurrentTab(location.pathname);

	const jobById = useQuery(
		jobQueryOptions.detail(params.jobId, { organizationId: params.orgId }),
	);

	const canDeleteJobs = useQuery({
		...authQueryOptions.checkAction({
			name: "DeleteJob",
			entityType: "Job",
			entityId: params.jobId,
		}),
		enabled: !!params.jobId,
	});

	useEffect(() => {
		setIsOpen(true);
	}, []);

	const drawerMetadata = useMemo(() => {
		const job = jobById.data;
		if (!job) return [];

		const items: {
			id: string;
			label: string;
			icon: typeof Hash01;
		}[] = [];

		if (job.jobNumber != null) {
			items.push({
				id: "job-number",
				label: String(job.jobNumber),
				icon: Hash01,
			});
		}
		if (job.createdAt) {
			items.push({
				id: "created-at",
				label: createdMetadataLabel(t, job.createdAt),
				icon: Calendar,
			});
		}
		if (job.updatedAt) {
			items.push({
				id: "updated-at",
				label: updatedMetadataLabel(t, job.updatedAt),
				icon: CalendarCheck01,
			});
		}
		return items;
	}, [jobById.data, t]);

	const handleClose = () => {
		setIsOpen(false);
		setTimeout(() => {
			navigate({
				to: `/web/$orgId/jobs`,
				params: { orgId: params.orgId },
				replace: true,
			});
		}, 200);
	};

	const handleDelete = () => {
		navigate({
			to: location.pathname,
			search: (prev) => ({ ...prev, delete: true }),
		});
		setIsDeleteModalOpen(true);
	};

	const handleOpenWorkspace = () => {
		navigate({
			to: "/web/$orgId/jobs/$jobId/workspace",
			params: { orgId: params.orgId, jobId: params.jobId },
		});
	};

	const handleOpenChange = (open: boolean) => {
		setIsOpen(open);
		if (!open) {
			handleClose();
		}
	};

	const title = jobById.data?.name
		? jobById.data?.name
		: t("main.jobs.drawer.title");

	const detailsTabs = [
		{
			label: t("main.jobs.drawer.tabs.details.label"),
			id: DetailsTab.Details,
		},
		{
			label: t("main.jobs.drawer.tabs.assignees.label"),
			id: DetailsTab.Assignee,
		},
		{
			label: t("main.jobs.drawer.tabs.activity.label"),
			id: DetailsTab.Activity,
		},
	];

	const handleTabChange = (tabId: string) => {
		if (tabId === currentTab) return;
		navigate({
			to: `/web/$orgId/jobs/$jobId/${tabId}`,
			params: {
				orgId: params.orgId,
				jobId: params.jobId,
			},
			search: (prev) => prev,
		});
	};

	const assigneeType: AssigneeTypeFilterValue =
		search.assigneeType === "users"
			? AssigneeTypeFilter.Users
			: search.assigneeType === "subcontractors"
				? AssigneeTypeFilter.Subcontractors
				: AssigneeTypeFilter.All;

	const handleAssigneeTypeChange = (next: AssigneeTypeFilterValue) => {
		void navigate({
			to: "/web/$orgId/jobs/$jobId/assignee",
			params: {
				orgId: params.orgId,
				jobId: params.jobId,
			},
			search: {
				organizationId: search.organizationId,
				jobId: search.jobId,
				delete: search.delete,
				tab: search.tab,
				assigneeType: next === AssigneeTypeFilter.All ? undefined : next,
			},
			replace: true,
		});
	};

	const clearDeleteSearch = () => {
		navigate({
			to: location.pathname,
			search: (prev) => {
				const { delete: _, ...rest } = prev;
				return rest;
			},
		});
		setIsDeleteModalOpen(false);
	};

	return (
		<DestinyEntityDrawer
			type="job"
			isOpen={isOpen}
			onOpenChange={handleOpenChange}
			title={title}
			metadata={drawerMetadata}
			isError={jobById.isError}
			tabs={detailsTabs}
			currentTab={currentTab}
			onTabChange={handleTabChange}
			actions={[
				{
					label: t("main.jobs.workspace.action"),
					icon: Maximize02,
					color: "secondary",
					onPress: handleOpenWorkspace,
				},
			]}
			overflowActions={
				canDeleteJobs.data
					? [
							{
								label: t("main.jobs.drawer.deleteJob.button"),
								icon: Trash01,
								onPress: handleDelete,
							},
						]
					: []
			}
			details={
				<JobEntityDetails
					orgId={params.orgId}
					jobId={params.jobId}
					state={
						jobById.isPending ? DetailsState.Loading : DetailsState.Default
					}
					selectedTabId={currentTab}
					onTabChange={handleTabChange}
					tabs={detailsTabs}
					className="max-md:[&_[role=tablist]]:hidden"
					assigneeType={assigneeType}
					onAssigneeTypeChange={handleAssigneeTypeChange}
				/>
			}
			outlet={<Outlet />}
		>
			{({ close }) => (
				<DeleteJobModal
					isOpen={isDeleteModalOpen || !!search.delete}
					setIsOpen={(open) => {
						if (!open) clearDeleteSearch();
					}}
					handleClose={clearDeleteSearch}
					onDeleteSuccess={() => {
						close();
						handleClose();
					}}
				/>
			)}
		</DestinyEntityDrawer>
	);
}
